import React, { useCallback } from 'react';
import { combineClasses } from '../../../utils/formatters';
import { TOPBAR_CONFIG } from '../../../config/topbarConfig';
import { SearchIcon, SearchIconMobile } from '../../ui/SvgIcons';

// Search Form Component - Optimized
export const SearchForm = React.memo(({ 
  searchTerm, 
  onSearchChange, 
  onSubmit
}) => (
  <form onSubmit={onSubmit} className={TOPBAR_CONFIG.COMPONENTS.searchInput.container}>
    <div className={TOPBAR_CONFIG.COMPONENTS.searchInput.iconPosition}>
      <SearchIcon className={TOPBAR_CONFIG.COMPONENTS.searchInput.iconColor} />
    </div>
    <input
      type="text"
      value={searchTerm}
      onChange={onSearchChange}
      placeholder={TOPBAR_CONFIG.SEARCH.placeholder}
      className={combineClasses(
        TOPBAR_CONFIG.COMPONENTS.display.block,
        "w-full",
        TOPBAR_CONFIG.SEARCH.styling.padding,
        TOPBAR_CONFIG.SEARCH.styling.text,
        TOPBAR_CONFIG.SEARCH.styling.border,
        TOPBAR_CONFIG.SEARCH.styling.background,
        TOPBAR_CONFIG.SEARCH.styling.placeholder,
        TOPBAR_CONFIG.COMPONENTS.misc.focusOutline,
        TOPBAR_CONFIG.SEARCH.styling.focus
      )}
      aria-label={TOPBAR_CONFIG.SEARCH.placeholder}
    />
  </form>
));

SearchForm.displayName = 'SearchForm';

// Desktop Search Component - Optimized
export const TopBarSearch = React.memo(({ 
  showSearch, 
  searchTerm, 
  setSearchTerm, 
  handleSearch
}) => {
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, [setSearchTerm]);

  if (!showSearch) return null;

  return (
    <div className={combineClasses(
      TOPBAR_CONFIG.COMPONENTS.layout.searchContainer,
      TOPBAR_CONFIG.SEARCH.visibility.desktop,
      TOPBAR_CONFIG.SEARCH.width.large,
      TOPBAR_CONFIG.SEARCH.width.extraLarge
    )}>
      <SearchForm
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onSubmit={handleSearch}
      />
    </div>
  );
});

TopBarSearch.displayName = 'TopBarSearch';

// Mobile Search Button Component - Optimized
export const TopBarSearchButton = React.memo(({ 
  showSearch, 
  showMobileSearch, 
  setShowMobileSearch 
}) => {
  const handleToggleMobileSearch = useCallback(() => {
    setShowMobileSearch(prev => !prev);
  }, [setShowMobileSearch]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleMobileSearch();
    }
  }, [handleToggleMobileSearch]);

  if (!showSearch) return null;

  return (
    <button
      onClick={handleToggleMobileSearch}
      onKeyDown={handleKeyDown}
      className={combineClasses(
        TOPBAR_CONFIG.COMPONENTS.button.search,
        TOPBAR_CONFIG.SEARCH.visibility.mobile,
        `text-${TOPBAR_CONFIG.COLORS.secondary}`,
        TOPBAR_CONFIG.HOVER_EFFECTS.button,
        TOPBAR_CONFIG.TRANSITIONS.default
      )}
      aria-label="Toggle mobile search"
      aria-expanded={showMobileSearch}
    >
      <SearchIconMobile />
    </button>
  );
});

TopBarSearchButton.displayName = 'TopBarSearchButton';

// Mobile Search Bar Component - Optimized
export const TopBarMobileSearch = React.memo(({ 
  showSearch, 
  showMobileSearch, 
  searchTerm, 
  setSearchTerm, 
  handleSearch
}) => {
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, [setSearchTerm]);

  if (!showSearch || !showMobileSearch) return null;

  return (
    <div className={combineClasses(
      `border-t border-${TOPBAR_CONFIG.COLORS.border}`,
      TOPBAR_CONFIG.SEARCH.visibility.mobile,
      TOPBAR_CONFIG.LAYOUT.padding.mobile,
      "py-3"
    )}>
      <SearchForm
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onSubmit={handleSearch}
      />
    </div>
  );
});

TopBarMobileSearch.displayName = 'TopBarMobileSearch';
